/**
 * Error Handling Utilities for Piczoo Kiosk
 * Provides consistent error handling, retry logic, and user-friendly messages
 */

// Error types
export const ERROR_TYPES = {
  CAMERA: 'camera',
  NETWORK: 'network',
  PRINT: 'print',
  PAYMENT: 'payment',
  SERVER: 'server',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown'
};

// Error codes with user-friendly messages
export const ERROR_MESSAGES = {
  // Camera errors
  CAMERA_PERMISSION_DENIED: {
    title: 'Camera Access Denied',
    message: 'Please allow camera access to take photos.',
    action: 'Check browser permissions and try again.',
    recoverable: true
  },
  CAMERA_NOT_FOUND: {
    title: 'No Camera Found',
    message: 'Could not find a camera on this device.',
    action: 'Retrying automatically — it will resume by itself once the camera is back.\nIf this persists, staff have been notified.',
    recoverable: false
  },
  CAMERA_IN_USE: {
    title: 'Camera In Use',
    message: 'The camera is being used by another application.',
    action: 'Close other apps using the camera and try again.',
    recoverable: true
  },
  CAMERA_STREAM_LOST: {
    title: 'Camera Disconnected',
    message: 'Lost connection to the camera.',
    action: 'Reconnecting...',
    recoverable: true
  },
  
  // Network errors
  NETWORK_OFFLINE: {
    title: 'No Internet Connection',
    message: 'Please check your internet connection.',
    action: 'Waiting for connection...',
    recoverable: true
  },
  NETWORK_TIMEOUT: {
    title: 'Connection Timeout',
    message: 'The server took too long to respond.',
    action: 'Trying again...',
    recoverable: true
  },
  NETWORK_ERROR: {
    title: 'Connection Error',
    message: 'Could not connect to the server.',
    action: 'Please try again.',
    recoverable: true
  },
  
  // Print errors
  PRINTER_NOT_FOUND: {
    title: 'Printer Not Found',
    message: 'The configured printer is not connected.',
    action: 'Please check the printer connection.',
    recoverable: false
  },
  PRINTER_OFFLINE: {
    title: 'Printer Offline',
    message: 'The printer is not responding.',
    action: 'Please turn on the printer and try again.',
    recoverable: true
  },
  PRINTER_PAPER_OUT: {
    title: 'Paper Out',
    message: 'The printer is out of paper.',
    action: 'Please load paper and try again.',
    recoverable: true
  },
  PRINT_FAILED: {
    title: 'Print Failed',
    message: 'Could not print your photos.',
    action: 'Please try again or contact staff.',
    recoverable: true
  },
  
  // Payment errors
  PAYMENT_DECLINED: {
    title: 'Payment Declined',
    message: 'Your payment was declined.',
    action: 'Please try a different payment method.',
    recoverable: true
  },
  PAYMENT_TIMEOUT: {
    title: 'Payment Timeout',
    message: 'Payment took too long to process.',
    action: 'Please try again.',
    recoverable: true
  },
  PAYMENT_CANCELLED: {
    title: 'Payment Cancelled',
    message: 'Payment was cancelled.',
    action: 'Press "Pay Now" to try again.',
    recoverable: true
  },
  
  // Server errors
  SERVER_ERROR: {
    title: 'Server Error',
    message: 'Something went wrong on our end.',
    action: 'Please try again in a moment.',
    recoverable: true
  },
  RATE_LIMITED: {
    title: 'Too Many Requests',
    message: 'Please slow down and try again.',
    action: 'Wait a moment before trying again.',
    recoverable: true
  },
  
  // Generic
  UNKNOWN_ERROR: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    action: 'Please try again.',
    recoverable: true
  }
};

/**
 * Parse error from various sources into standardized format
 */
export function parseError(error) {
  // Network/fetch errors
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return {
      code: 'NETWORK_ERROR',
      type: ERROR_TYPES.NETWORK,
      ...ERROR_MESSAGES.NETWORK_ERROR
    };
  }
  
  // Axios errors
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    if (status === 429) {
      return {
        code: 'RATE_LIMITED',
        type: ERROR_TYPES.SERVER,
        ...ERROR_MESSAGES.RATE_LIMITED
      };
    }
    
    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        type: ERROR_TYPES.SERVER,
        ...ERROR_MESSAGES.SERVER_ERROR
      };
    }
    
    if (status === 401 || status === 403) {
      return {
        code: 'AUTH_ERROR',
        type: ERROR_TYPES.SERVER,
        title: 'Authentication Error',
        message: data.detail || 'Please log in again.',
        action: 'Refreshing session...',
        recoverable: true
      };
    }
    
    // Validation errors
    if (status === 400 || status === 422) {
      return {
        code: 'VALIDATION_ERROR',
        type: ERROR_TYPES.VALIDATION,
        title: 'Invalid Request',
        message: data.detail || data.message || 'Please check your input.',
        action: 'Correct the error and try again.',
        recoverable: true
      };
    }
  }
  
  // Offline
  if (!navigator.onLine) {
    return {
      code: 'NETWORK_OFFLINE',
      type: ERROR_TYPES.NETWORK,
      ...ERROR_MESSAGES.NETWORK_OFFLINE
    };
  }
  
  // Camera errors
  if (error.name === 'NotAllowedError') {
    return {
      code: 'CAMERA_PERMISSION_DENIED',
      type: ERROR_TYPES.CAMERA,
      ...ERROR_MESSAGES.CAMERA_PERMISSION_DENIED
    };
  }
  
  if (error.name === 'NotFoundError') {
    return {
      code: 'CAMERA_NOT_FOUND',
      type: ERROR_TYPES.CAMERA,
      ...ERROR_MESSAGES.CAMERA_NOT_FOUND
    };
  }
  
  if (error.name === 'NotReadableError') {
    return {
      code: 'CAMERA_IN_USE',
      type: ERROR_TYPES.CAMERA,
      ...ERROR_MESSAGES.CAMERA_IN_USE
    };
  }
  
  // Default
  return {
    code: 'UNKNOWN_ERROR',
    type: ERROR_TYPES.UNKNOWN,
    ...ERROR_MESSAGES.UNKNOWN_ERROR,
    originalError: error.message
  };
}

/**
 * Retry with exponential backoff
 */
export async function retryWithBackoff(
  fn,
  options = {}
) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    shouldRetry = () => true,
    onRetry = () => {}
  } = options;
  
  let lastError;
  let delay = initialDelay;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }
      
      onRetry(attempt + 1, error, delay);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * backoffFactor, maxDelay);
    }
  }
  
  throw lastError;
}

/**
 * Network status monitoring
 */
export class NetworkMonitor {
  constructor(onChange) {
    this.onChange = onChange;
    this.isOnline = navigator.onLine;
    
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }
  
  handleOnline = () => {
    this.isOnline = true;
    this.onChange?.(true);
  };
  
  handleOffline = () => {
    this.isOnline = false;
    this.onChange?.(false);
  };
  
  destroy() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
}

/**
 * Camera error recovery
 */
export async function recoverCameraStream(videoRef, streamRef, constraints = {}) {
  const defaultConstraints = {
    video: {
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      facingMode: 'user'
    },
    audio: false
  };
  
  const finalConstraints = { ...defaultConstraints, ...constraints };
  
  // Stop existing stream
  if (streamRef.current) {
    streamRef.current.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }
  
  // Clear video element
  if (videoRef.current) {
    videoRef.current.srcObject = null;
  }
  
  // Wait a moment for cleanup
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Try to get new stream
  const stream = await navigator.mediaDevices.getUserMedia(finalConstraints);
  streamRef.current = stream;
  
  if (videoRef.current) {
    videoRef.current.srcObject = stream;
    await videoRef.current.play();
  }
  
  return stream;
}

/**
 * Session timeout manager
 */
export class SessionTimeoutManager {
  constructor(options = {}) {
    this.timeout = options.timeout || 5 * 60 * 1000; // 5 minutes default
    this.warningTime = options.warningTime || 60 * 1000; // 1 minute warning
    this.onWarning = options.onWarning || (() => {});
    this.onTimeout = options.onTimeout || (() => {});
    
    this.timer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();
    
    this.boundResetTimer = this.resetTimer.bind(this);
    
    // Track user activity
    ['mousedown', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, this.boundResetTimer, { passive: true });
    });
    
    this.startTimer();
  }
  
  startTimer() {
    this.clearTimers();
    
    // Warning timer
    this.warningTimer = setTimeout(() => {
      this.onWarning(this.warningTime / 1000);
    }, this.timeout - this.warningTime);
    
    // Timeout timer
    this.timer = setTimeout(() => {
      this.onTimeout();
    }, this.timeout);
  }
  
  resetTimer() {
    this.lastActivity = Date.now();
    this.startTimer();
  }
  
  clearTimers() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }
  
  destroy() {
    this.clearTimers();
    ['mousedown', 'touchstart', 'keydown'].forEach(event => {
      document.removeEventListener(event, this.boundResetTimer);
    });
  }
  
  getRemainingTime() {
    return Math.max(0, this.timeout - (Date.now() - this.lastActivity));
  }
}

/**
 * Offline queue for photos
 */
export class OfflinePhotoQueue {
  constructor(storageKey = 'piczoo_offline_photos') {
    this.storageKey = storageKey;
    this.queue = this.loadQueue();
  }
  
  loadQueue() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }
  
  saveQueue() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.queue));
    } catch (e) {
      console.error('Failed to save offline queue:', e);
    }
  }
  
  add(photos, metadata) {
    this.queue.push({
      id: `offline_${Date.now()}`,
      photos,
      metadata,
      timestamp: Date.now()
    });
    this.saveQueue();
  }
  
  getAll() {
    return [...this.queue];
  }
  
  remove(id) {
    this.queue = this.queue.filter(item => item.id !== id);
    this.saveQueue();
  }
  
  clear() {
    this.queue = [];
    this.saveQueue();
  }
  
  get length() {
    return this.queue.length;
  }
}

export default {
  ERROR_TYPES,
  ERROR_MESSAGES,
  parseError,
  retryWithBackoff,
  NetworkMonitor,
  recoverCameraStream,
  SessionTimeoutManager,
  OfflinePhotoQueue
};
